import { MORPH_HELPER_SCRIPT_ID, MORPH_HELPER_SOURCE } from './gsapMorphHelpers';
import { compositionAgentSource } from './compositionAgent';
import { SNAPDOM_SOURCE } from './snapdomInlineSource.generated';
import { GSAP_SCRIPT_ID } from './htmlManagement';

export const COMPOSITION_CSP = [
    "default-src 'none'",
    "script-src 'unsafe-inline'",
    "style-src 'unsafe-inline'",
    'img-src data: blob:',
    'font-src data:',
    'media-src data: blob:',
    "connect-src 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "object-src 'none'",
    "base-uri 'none'",
    "form-action 'none'",
].join('; ');

const CLOSING_SCRIPT_TAG = '</' + 'script>';

// gsap is ~160KB of vendored source, needed only when a composition actually renders. Loading it
// through a dynamic import keeps it in its own chunk (gsap-vendor) rather than the entry bundle,
// and — critically — that chunk is fetched from the same origin as luminary itself (locally, on
// disk, in the nwjs render worker), so it survives the render pods' locked-down egress. The old
// path pulled gsap from cdnjs, which the render workers' Istio egress does not allow, so every
// composition render timed out on the missing dependency.
let gsapSourcePromise: Promise<string> | null = null;

async function loadGsapSource(): Promise<string> {
    if (!gsapSourcePromise) {
        gsapSourcePromise = import(
            /* webpackChunkName: "gsap-vendor" */ './gsapInlineSource.generated'
        ).then(
            (module) => module.GSAP_SOURCE,
            (error: unknown) => {
                // A rejected import must not poison the memo. In the editor this chunk is fetched
                // from the GCS publicPath, so a transient network failure would otherwise leave
                // every later build rejecting and defeat HtmlCompositionRenderer.load()'s own retry.
                // Clear it so the next call re-imports.
                gsapSourcePromise = null;
                throw error;
            },
        );
    }
    return gsapSourcePromise;
}

let geoSourcePromise: Promise<string> | null = null;

async function loadGeoSource(): Promise<string> {
    if (!geoSourcePromise) {
        geoSourcePromise = import(
            /* webpackChunkName: "geo-vendor" */ './geoInlineSource.generated'
        ).then(
            (module) => module.GEO_SOURCE,
            (error: unknown) => {
                geoSourcePromise = null;
                throw error;
            },
        );
    }
    return geoSourcePromise;
}

function nonceAttribute(nonce: string | undefined): string {
    return nonce ? ` nonce="${nonce}"` : '';
}

/**
 * Stamp the host nonce onto the author's own inline `<script>`/`<style>`, which a nonce-based host
 * CSP would otherwise block.
 *
 * Scoped to author markup on purpose. Run over the whole assembled document it also rewrites the
 * inlined snapDOM body, whose minified source contains `<style[^>]*>` regex literals that this
 * pattern matches — leaving snapDOM unable to match a real `<style>` tag. Everything we inject gets
 * its nonce as an attribute at construction instead.
 */
export function applyCspNonce(html: string, nonce: string | undefined): string {
    if (!nonce) {
        return html;
    }
    return html
        .replace(/<script\b(?![^>]*\bnonce=)/gi, `<script nonce="${nonce}"`)
        .replace(/<style\b(?![^>]*\bnonce=)/gi, `<style nonce="${nonce}"`);
}

export const AGENT_SCRIPT_ID = 'l5-agent';
export const SNAPDOM_SCRIPT_ID = 'l5-snapdom';
export const GEO_SCRIPT_ID = 'l5-geo';

const USES_GEO_RUNTIME = /\bL5Geo\b/;

const AUTHOR_GSAP_SCRIPT = /<script\b[^>]*\bsrc="[^"]*gsap[^"]*"[^>]*>\s*<\/script>/gi;
const EXISTING_MORPH_HELPER = new RegExp(
    `<script id="${MORPH_HELPER_SCRIPT_ID}">[\\s\\S]*?${CLOSING_SCRIPT_TAG}`,
    'i',
);
// The vendored gsap is a single inline `<script id="l5-gsap">…</script>` with no `src`, so the
// author-gsap regex above cannot strip it on a re-assembly. Strip it by id, the way the morph
// helper is stripped, to keep buildCompositionSrcdoc idempotent.
const EXISTING_INLINE_GSAP = new RegExp(
    `<script id="${GSAP_SCRIPT_ID}"[^>]*>[\\s\\S]*?${CLOSING_SCRIPT_TAG}`,
    'i',
);
const EXISTING_INLINE_GEO = new RegExp(
    `<script id="${GEO_SCRIPT_ID}"[^>]*>[\\s\\S]*?${CLOSING_SCRIPT_TAG}`,
    'i',
);

export async function buildCompositionSrcdoc(
    managedHtml: string,
    agentToken?: string,
    nonce?: string,
    hostOrigin = location.origin,
): Promise<string> {
    const authorHtml = managedHtml
        .replace(AUTHOR_GSAP_SCRIPT, '')
        .replace(EXISTING_INLINE_GSAP, '')
        .replace(EXISTING_MORPH_HELPER, '')
        .replace(EXISTING_INLINE_GEO, '');
    const [gsapSource, geoSource] = await Promise.all([
        loadGsapSource(),
        USES_GEO_RUNTIME.test(authorHtml) ? loadGeoSource() : null,
    ]);
    const withoutInjected = applyCspNonce(authorHtml, nonce);
    const attribute = nonceAttribute(nonce);
    const geo =
        geoSource === null
            ? ''
            : `<script id="${GEO_SCRIPT_ID}"${attribute}>${geoSource}${CLOSING_SCRIPT_TAG}`;

    const agent = agentToken
        ? `<script id="${SNAPDOM_SCRIPT_ID}"${attribute}>(() => {\n${SNAPDOM_SOURCE}\n})();` +
          `${CLOSING_SCRIPT_TAG}` +
          `<script id="${AGENT_SCRIPT_ID}"${attribute}>` +
          `${compositionAgentSource(agentToken, hostOrigin)}${CLOSING_SCRIPT_TAG}`
        : '';

    const bootstrap = [
        '<!doctype html>',
        '<meta charset="utf-8">',
        `<meta http-equiv="Content-Security-Policy" content="${COMPOSITION_CSP}">`,
        agent,
        `<script id="${GSAP_SCRIPT_ID}"${attribute}>${gsapSource}${CLOSING_SCRIPT_TAG}`,
        `<script id="${MORPH_HELPER_SCRIPT_ID}"${attribute}>` +
            `${MORPH_HELPER_SOURCE}${CLOSING_SCRIPT_TAG}`,
        geo,
    ].join('');

    return `${bootstrap}${withoutInjected}`;
}
